import { Injectable } from '@angular/core';
import { Observable, firstValueFrom, from, of, timer } from 'rxjs';
import { catchError, map, shareReplay, timeout } from 'rxjs/operators';
import { ApiService } from './api.service';
import { VariantService } from './variant.service';

const MAX_ASSIGN_ATTEMPTS = 4;
const ASSIGN_RETRY_DELAY_MS = 500;

export interface InsuranceVariantAssignment {
  ga_session_id: string;
  variant_id: number;
  version_code: string;
  version_name: string;
  assigned_at: string;
  reassigned: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class InsuranceVariantService {
  private readonly cacheKey = 'insurance_variant.assignment';
  private readonly testOverrideKey = 'insurance_variant.test_override';
  private assignmentRequest$: Observable<InsuranceVariantAssignment> | null = null;

  constructor(
    private api: ApiService,
    private variantService: VariantService
  ) {}

  getCachedAssignment(): InsuranceVariantAssignment | null {
    try {
      const cached = this.readCachedAssignmentRaw();
      if (!cached) return null;
      let assignment = JSON.parse(cached.raw) as InsuranceVariantAssignment;
      if (!this.normalizeInsuranceCode(assignment?.version_code)) {
        this.clearVariantAssignment();
        return null;
      }

      const currentGaSessionId = this.getGaSessionId();
      const cachedGaSessionId = (assignment?.ga_session_id || '').toString().trim();
      if (currentGaSessionId && cachedGaSessionId && currentGaSessionId !== cachedGaSessionId) {
        this.clearVariantAssignment();
        return null;
      }

      const override = this.readTestVariantOverride();
      if (override) {
        assignment = this.applyTestAssignment(assignment, override);
        this.persistAssignment(assignment);
      }
      return assignment;
    } catch {
      return null;
    }
  }

  getCachedVariantCode(): string | null {
    try {
      const assignment = this.getCachedAssignment();
      return assignment?.version_code ? assignment.version_code : null;
    } catch {
      return null;
    }
  }

  preloadDetailsMeVariant(insuranceVariantCode: string | null | undefined): void {
    import('../../variants/mandatory/details-me/default/details-me').catch(() => {});
  }

  ensureAssignmentCached(context: string = 'route'): Observable<InsuranceVariantAssignment> {
    const cachedAssignment = this.getCachedAssignment();
    if (cachedAssignment) {
      // index.html verifies the cached variant through the assign API (reassigning if it went
      // inactive) and rewrites the cache; wait for that so we never use a stale variant.
      const startup = this.readStartupPromise();
      if (!startup) return of(cachedAssignment);
      return from(startup).pipe(
        map(() => this.getCachedAssignment() || cachedAssignment),
        catchError(() => of(cachedAssignment))
      );
    }

    const startupAssignment = this.readStartupAssignmentPromise();
    if (startupAssignment) {
      return startupAssignment;
    }

    return this.assignVariantFromApi(context);
  }

  assignVariantFromApi(context: string = 'direct'): Observable<InsuranceVariantAssignment> {
    if (this.assignmentRequest$) {
      return this.assignmentRequest$;
    }

    const gaSessionId = this.getGaSessionId();
    if (!gaSessionId) {
      return of(this.persistAssignFailureFallback('', false));
    }

    this.assignmentRequest$ = from(this.assignWithRetries(gaSessionId)).pipe(
      map((assignment) => {
        const out = this.withOverrideIfNeeded(this.normalizeAssignment(assignment, gaSessionId));
        this.persistAssignment(out);
        this.assignmentRequest$ = null;
        try {
          this.preloadDetailsMeVariant(out.version_code);
        } catch {}
        return out;
      }),
      catchError((error) => {
        this.assignmentRequest$ = null;
        console.error('Insurance variant assignment API failed, using persisted fallback:', error);
        return of(this.persistAssignFailureFallback(gaSessionId, false));
      }),
      shareReplay(1)
    );

    return this.assignmentRequest$;
  }

  assignVariant(): Observable<InsuranceVariantAssignment> {
    return this.ensureAssignmentCached('assignVariant');
  }

  clearVariantAssignment(): void {
    try {
      localStorage.removeItem(this.cacheKey);
      sessionStorage.removeItem(this.cacheKey);
      this.assignmentRequest$ = null;
    } catch (error) {
      console.warn('Failed to clear insurance variant assignment:', error);
    }
  }

  clearAll(): void {
    try {
      localStorage.removeItem(this.cacheKey);
      sessionStorage.removeItem(this.cacheKey);
      sessionStorage.removeItem(this.testOverrideKey);
      this.assignmentRequest$ = null;
    } catch (error) {
      console.warn('Failed to clear insurance variant data:', error);
    }
  }

  private async assignWithRetries(gaSessionId: string): Promise<InsuranceVariantAssignment> {
    let lastError: unknown = null;
    for (let attempt = 1; attempt <= MAX_ASSIGN_ATTEMPTS; attempt++) {
      const isFinalAttempt = attempt >= MAX_ASSIGN_ATTEMPTS;
      const payload = {
        ga_session_id: gaSessionId,
        browser_info: this.getBrowserInfo(),
        is_final_attempt: isFinalAttempt
      };
      try {
        return await firstValueFrom(
          this.api.dispatch<InsuranceVariantAssignment>('/insurance_variants/assign', payload).pipe(
            timeout(10000)
          )
        );
      } catch (error) {
        lastError = error;
        if (isFinalAttempt) {
          throw error;
        }
        try {
          await firstValueFrom(timer(ASSIGN_RETRY_DELAY_MS * attempt));
        } catch {}
      }
    }
    throw lastError || new Error('Insurance variant assignment failed');
  }

  private getGaSessionId(): string {
    try {
      return this.variantService.getSessionUuid();
    } catch {
      return '';
    }
  }

  private readStartupPromise(): Promise<unknown> | null {
    try {
      if (typeof window === 'undefined') return null;
      const p = (window as any).__olsInsuranceVariantAssignmentPromise;
      return p && typeof p.then === 'function' ? p : null;
    } catch {
      return null;
    }
  }

  private readStartupAssignmentPromise(): Observable<InsuranceVariantAssignment> | null {
    try {
      if (typeof window === 'undefined') return null;
      const startupPromise = (window as any).__olsInsuranceVariantAssignmentPromise;
      if (!startupPromise || typeof startupPromise.then !== 'function') return null;

      return from(startupPromise as Promise<InsuranceVariantAssignment | null>).pipe(
        map((assignment) => {
          if (!assignment) {
            throw new Error('OLS startup insurance variant assignment was empty.');
          }
          const out = this.withOverrideIfNeeded(this.normalizeAssignment(assignment, this.getGaSessionId()));
          if (Number(out.variant_id) >= 0) {
            this.persistAssignment(out);
          }
          try {
            this.preloadDetailsMeVariant(out.version_code);
          } catch {}
          return out;
        }),
        catchError(() => of(this.persistAssignFailureFallback(this.getGaSessionId(), false)))
      );
    } catch {
      return null;
    }
  }

  private readCachedAssignmentRaw(): { raw: string; source: 'localStorage' | 'sessionStorage' } | null {
    try {
      const localRaw = localStorage.getItem(this.cacheKey);
      if (localRaw) return { raw: localRaw, source: 'localStorage' };
    } catch {}
    try {
      const sessionRaw = sessionStorage.getItem(this.cacheKey);
      if (sessionRaw) {
        try { localStorage.setItem(this.cacheKey, sessionRaw); } catch {}
        return { raw: sessionRaw, source: 'sessionStorage' };
      }
    } catch {}
    return null;
  }

  private persistAssignment(assignment: InsuranceVariantAssignment): void {
    try {
      const serialized = JSON.stringify(assignment);
      try { localStorage.setItem(this.cacheKey, serialized); } catch (error) {
        console.warn('Failed to write insurance variant cache:', error);
      }
      try { sessionStorage.setItem(this.cacheKey, serialized); } catch {}
    } catch (error) {
      console.warn('Failed to serialize insurance variant cache:', error);
    }
  }

  private normalizeAssignment(
    assignment: InsuranceVariantAssignment,
    gaSessionId: string
  ): InsuranceVariantAssignment {
    const code = this.normalizeInsuranceCode(assignment?.version_code) || 'scroll';
    const versionName = (assignment?.version_name || '').toString().trim() || this.getVersionName(code);
    return {
      ga_session_id: assignment?.ga_session_id || gaSessionId,
      variant_id: Number.isFinite(Number(assignment?.variant_id)) ? Number(assignment.variant_id) : -1,
      version_code: code,
      version_name: versionName,
      assigned_at: assignment?.assigned_at || new Date().toISOString(),
      reassigned: !!assignment?.reassigned
    };
  }

  private persistAssignFailureFallback(gaSessionId: string, shouldPersist: boolean = true): InsuranceVariantAssignment {
    const code = this.readTestVariantOverride() || 'scroll';
    const fallback: InsuranceVariantAssignment = {
      ga_session_id: gaSessionId,
      variant_id: -1,
      version_code: code,
      version_name: this.getVersionName(code),
      assigned_at: new Date().toISOString(),
      reassigned: false
    };
    if (shouldPersist) {
      this.persistAssignment(fallback);
    }
    try {
      this.preloadDetailsMeVariant(fallback.version_code);
    } catch {}
    return fallback;
  }

  private withOverrideIfNeeded(assignment: InsuranceVariantAssignment): InsuranceVariantAssignment {
    const override = this.readTestVariantOverride();
    if (!override) return assignment;
    return this.applyTestAssignment(assignment, override);
  }

  private applyTestAssignment(
    base: InsuranceVariantAssignment,
    code: 'scroll' | 'cards'
  ): InsuranceVariantAssignment {
    return {
      ...base,
      version_code: code,
      version_name: this.getVersionName(code)
    };
  }

  private readTestVariantOverride(): 'scroll' | 'cards' | null {
    try {
      if (typeof window === 'undefined') return null;
      const params = new URLSearchParams(window.location.search || '');
      const directRaw = (params.get('insurance_test_variant') || '').trim();
      const sharedRaw = (params.get('test_variant') || '').trim();
      const raw = directRaw || this.getSharedOverrideRaw(sharedRaw);
      const normalizedRaw = raw.toLowerCase().replace(/-/g, '_');

      if (normalizedRaw === 'off' || normalizedRaw === 'clear' || normalizedRaw === '0' || normalizedRaw === 'false') {
        sessionStorage.removeItem(this.testOverrideKey);
        return null;
      }

      const normalized = this.normalizeInsuranceCode(normalizedRaw);
      if (normalized) {
        sessionStorage.setItem(this.testOverrideKey, normalized);
        return normalized;
      }

      const stored = (sessionStorage.getItem(this.testOverrideKey) || '').trim();
      return this.normalizeInsuranceCode(stored);
    } catch {}
    return null;
  }

  private getSharedOverrideRaw(raw: string): string {
    const normalized = (raw || '').trim().toLowerCase().replace(/-/g, '_');
    if (normalized === 'scroll' || normalized === 'cards') return normalized;
    return '';
  }

  private normalizeInsuranceCode(value: string | null | undefined): 'scroll' | 'cards' | null {
    const code = (value || '').toString().trim().toLowerCase().replace(/-/g, '_');
    if (code === 'cards') return 'cards';
    if (code === 'scroll') return 'scroll';
    return null;
  }

  private getVersionName(code: 'scroll' | 'cards'): string {
    return code === 'cards' ? 'Cards' : 'Scroll';
  }

  private getBrowserInfo(): any {
    try {
      return {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height
      };
    } catch {
      return {};
    }
  }
}
